import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, LayoutDashboard, Loader2, Image as ImageIcon, X, Pencil, Trash2,
  Calendar, Clock, MapPin, ChevronLeft, ChevronRight,
} from 'lucide-react';

const EA_CSS = `
  .ea-root { font-family:'Outfit',sans-serif; background:#f7f9fc; min-height:100vh; padding:2.5rem 1.25rem 5rem; }
  .ea-shell { max-width:920px; margin:0 auto; }

  .ea-topnav { display:flex; justify-content:center; gap:.75rem; margin-bottom:2.5rem; }
  .ea-topbtn { display:inline-flex; align-items:center; gap:.5rem; padding:.6rem 1.25rem; border-radius:2px; font-size:.72rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; border:1px solid rgba(4,52,99,.15); background:#ffffff; color:#043463; cursor:pointer; transition:all .2s; }
  .ea-topbtn:hover { border-color:#ee4c05; color:#ee4c05; transform:translateY(-1px); }
  .ea-topbtn.primary { background:#043463; color:#fff; border-color:#043463; }
  .ea-topbtn.primary:hover { background:#0e7fbb; border-color:#0e7fbb; color:#fff; }

  .ea-h1 { font-family:'Bebas Neue',sans-serif; font-size:clamp(1.9rem,4vw,2.5rem); letter-spacing:.04em; color:#043463; text-align:center; margin-bottom:2rem; }
  .ea-h1 span { color:#ee4c05; }

  .ea-card { background:#ffffff; border:1px solid rgba(4,52,99,.1); border-radius:2px; box-shadow:0 4px 20px rgba(4,52,99,.04); }
  .ea-form { padding:clamp(1.5rem,3vw,2.5rem); margin-bottom:3rem; }
  .ea-form-h { font-family:'Bebas Neue',sans-serif; font-size:1.375rem; letter-spacing:.04em; color:#043463; margin-bottom:1.5rem; display:flex; align-items:center; gap:.6rem; }
  .ea-form-h .dot { width:8px; height:8px; border-radius:100px; background:#ee4c05; }

  .ea-field { margin-bottom:1.25rem; position:relative; }
  .ea-label { display:block; font-size:.65rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:#0e7fbb; margin-bottom:.5rem; }
  .ea-input, .ea-textarea { width:100%; padding:.75rem 1rem; border:1px solid rgba(4,52,99,.15); border-radius:2px; font-family:'Outfit',sans-serif; font-size:.9rem; color:#043463; background:#fff; transition:border-color .2s; }
  .ea-input:focus, .ea-textarea:focus { outline:none; border-color:#ee4c05; }
  .ea-textarea { min-height:100px; resize:vertical; }
  .ea-row2 { display:grid; grid-template-columns:1fr 1fr; gap:1.25rem; }
  .ea-row3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:1.25rem; }
  @media(max-width:600px){ .ea-row2, .ea-row3 { grid-template-columns:1fr; } }

  .ea-dropzone { border:1.5px dashed rgba(4,52,99,.25); border-radius:2px; padding:1.25rem; text-align:center; cursor:pointer; transition:border-color .2s, background .2s; position:relative; background:#fbfcfe; }
  .ea-dropzone:hover, .ea-dropzone.drag-active { border-color:#ee4c05; background:rgba(238,76,5,.03); }
  .ea-dropzone:focus-visible { outline:2px solid #0e7fbb; outline-offset:2px; }
  .ea-dropzone-input { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
  .ea-dropzone-icon { color:#0e7fbb; margin-bottom:.5rem; }
  .ea-dropzone-text { font-size:.78rem; color:#4a5568; }
  .ea-dropzone-text b { color:#043463; }

  .ea-thumbs { display:flex; flex-wrap:wrap; gap:.75rem; margin-top:.9rem; }
  .ea-thumb-wrap { position:relative; width:88px; height:88px; }
  .ea-thumb { width:88px; height:88px; object-fit:cover; border-radius:2px; display:block; background:#f0f4f8; border:1px solid rgba(4,52,99,.1); }
  .ea-thumb-remove { position:absolute; top:-6px; right:-6px; width:22px; height:22px; border-radius:100px; background:rgba(4,52,99,.85); color:#fff; border:none; display:flex; align-items:center; justify-content:center; cursor:pointer; }
  .ea-thumb-remove:hover { background:#ee4c05; }

  .ea-actions { display:flex; justify-content:flex-end; gap:.75rem; margin-top:1.75rem; }
  .ea-btn { padding:.7rem 1.5rem; border-radius:2px; font-size:.72rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; border:none; cursor:pointer; transition:all .2s; display:inline-flex; align-items:center; gap:.5rem; }
  .ea-btn-cancel { background:#eef1f5; color:#043463; }
  .ea-btn-cancel:hover { background:#e2e8f0; }
  .ea-btn-submit { background:#ee4c05; color:#fff; }
  .ea-btn-submit:hover { background:#043463; }
  .ea-btn-submit:disabled { opacity:.6; cursor:not-allowed; }

  .ea-list-h-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:.75rem; }
  .ea-list-h { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:.04em; color:#043463; }
  .ea-search { display:flex; align-items:center; gap:.6rem; background:#fff; border:1px solid rgba(4,52,99,.15); padding:.55rem 1rem; border-radius:2px; min-width:220px; }
  .ea-search input { border:none; outline:none; background:none; font-size:.82rem; color:#043463; width:100%; }

  .ea-list { display:flex; flex-direction:column; gap:.9rem; }
  .ea-item { padding:1.1rem 1.25rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; transition:border-color .2s; }
  .ea-item:hover { border-color:rgba(238,76,5,.35); }
  .ea-item-left { min-width:0; }
  .ea-item-top { display:flex; align-items:center; gap:.6rem; flex-wrap:wrap; margin-bottom:.35rem; }
  .ea-badge { font-size:.58rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; padding:.25rem .6rem; border-radius:100px; }
  .ea-badge.upcoming { background:rgba(14,127,187,.1); color:#0e7fbb; }
  .ea-badge.past { background:rgba(4,52,99,.08); color:#4a5568; }
  .ea-name { font-family:'Bebas Neue',sans-serif; font-size:1.05rem; letter-spacing:.03em; color:#043463; line-height:1.15; }
  .ea-meta { display:flex; flex-wrap:wrap; gap:1rem; margin-top:.4rem; }
  .ea-meta-item { display:flex; align-items:center; gap:.35rem; font-size:.75rem; color:#4a5568; }

  .ea-item-actions { display:flex; gap:.5rem; flex-shrink:0; }
  .ea-mini-btn { display:inline-flex; align-items:center; gap:.3rem; padding:.4rem .7rem; border-radius:2px; font-size:.65rem; font-weight:800; letter-spacing:.05em; text-transform:uppercase; border:none; cursor:pointer; transition:all .2s; }
  .ea-mini-edit { background:rgba(14,127,187,.1); color:#0e7fbb; }
  .ea-mini-edit:hover { background:#0e7fbb; color:#fff; }
  .ea-mini-del { background:rgba(238,76,5,.1); color:#ee4c05; }
  .ea-mini-del:hover { background:#ee4c05; color:#fff; }

  .ea-empty { text-align:center; padding:3.5rem 1rem; color:#4a5568; font-size:.875rem; }

  .ea-toast { position:fixed; top:1.5rem; left:50%; transform:translateX(-50%); z-index:60; padding:.8rem 1.5rem; border-radius:2px; font-size:.8rem; font-weight:600; color:#fff; box-shadow:0 10px 30px rgba(4,52,99,.2); }
  .ea-toast.success { background:#043463; }
  .ea-toast.error { background:#ee4c05; }

  .ea-overlay { position:fixed; inset:0; background:rgba(4,52,99,.5); display:flex; align-items:center; justify-content:center; z-index:65; }
  .ea-spinner { color:#ee4c05; }

  .ea-modal-backdrop { position:fixed; inset:0; background:rgba(4,52,99,.55); display:flex; align-items:center; justify-content:center; z-index:70; padding:1rem; }
  .ea-modal { background:#fff; border-radius:2px; padding:2rem; max-width:360px; width:100%; text-align:center; }
  .ea-modal h4 { font-family:'Bebas Neue',sans-serif; font-size:1.375rem; letter-spacing:.03em; color:#ee4c05; margin-bottom:.75rem; }
  .ea-modal p { font-size:.85rem; color:#4a5568; margin-bottom:1.5rem; }
  .ea-modal-actions { display:flex; justify-content:center; gap:.75rem; }

  /* ---- Custom date / time pickers ---- */
  .ea-pick-trigger { width:100%; display:flex; align-items:center; justify-content:space-between; gap:.5rem; padding:.75rem 1rem; border:1px solid rgba(4,52,99,.15); border-radius:2px; font-family:'Outfit',sans-serif; font-size:.9rem; color:#043463; background:#fff; cursor:pointer; transition:border-color .2s; text-align:left; }
  .ea-pick-trigger:hover { border-color:#0e7fbb; }
  .ea-pick-trigger.open { border-color:#ee4c05; }
  .ea-pick-trigger .ph { color:#9aa7b8; }
  .ea-pick-trigger svg { color:#0e7fbb; flex-shrink:0; }

  .ea-popover { position:absolute; z-index:40; top:calc(100% + .5rem); left:0; background:#fff; border:1px solid rgba(4,52,99,.12); border-radius:2px; box-shadow:0 16px 40px rgba(4,52,99,.16); animation:eaPop .14s ease; }
  @keyframes eaPop { from{ opacity:0; transform:translateY(-4px); } to{ opacity:1; transform:translateY(0); } }

  /* calendar */
  .ea-cal { width:280px; padding:1rem; }
  .ea-cal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:.85rem; }
  .ea-cal-title { font-family:'Bebas Neue',sans-serif; font-size:1.05rem; letter-spacing:.03em; color:#043463; }
  .ea-cal-nav { display:flex; gap:.25rem; }
  .ea-cal-navbtn { width:26px; height:26px; display:flex; align-items:center; justify-content:center; border:none; background:#eef1f5; color:#043463; border-radius:100px; cursor:pointer; transition:all .15s; }
  .ea-cal-navbtn:hover { background:#0e7fbb; color:#fff; }
  .ea-cal-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:2px; }
  .ea-cal-dow { text-align:center; font-size:.6rem; font-weight:800; letter-spacing:.05em; text-transform:uppercase; color:#0e7fbb; padding-bottom:.4rem; }
  .ea-cal-day { aspect-ratio:1; display:flex; align-items:center; justify-content:center; font-size:.8rem; color:#043463; background:none; border:none; border-radius:100px; cursor:pointer; transition:all .12s; }
  .ea-cal-day:hover:not(:disabled) { background:rgba(14,127,187,.12); }
  .ea-cal-day.muted { color:#c3cbd6; }
  .ea-cal-day.today { box-shadow:inset 0 0 0 1.5px #0e7fbb; }
  .ea-cal-day.selected { background:#ee4c05; color:#fff; font-weight:700; }
  .ea-cal-day.selected:hover { background:#ee4c05; }
  .ea-cal-day:disabled { color:#e1e6ec; cursor:not-allowed; }
  .ea-cal-today-row { margin-top:.75rem; padding-top:.75rem; border-top:1px solid rgba(4,52,99,.08); text-align:center; }
  .ea-cal-today-btn { font-size:.65rem; font-weight:800; letter-spacing:.06em; text-transform:uppercase; background:none; border:none; color:#0e7fbb; cursor:pointer; }
  .ea-cal-today-btn:hover { color:#ee4c05; }

  /* time picker */
  .ea-time { width:220px; padding:.9rem; }
  .ea-time-face { display:flex; align-items:center; justify-content:center; gap:.4rem; margin-bottom:.75rem; }
  .ea-time-clockicon { color:#ee4c05; }
  .ea-time-display { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:.03em; color:#043463; }
  .ea-time-cols { display:flex; gap:.4rem; height:168px; }
  .ea-time-col { flex:1; overflow-y:auto; scroll-snap-type:y mandatory; border-radius:2px; background:#fbfcfe; scrollbar-width:thin; }
  .ea-time-col::-webkit-scrollbar { width:5px; }
  .ea-time-col::-webkit-scrollbar-thumb { background:rgba(4,52,99,.15); border-radius:100px; }
  .ea-time-pad { height:66px; flex-shrink:0; }
  .ea-time-opt { scroll-snap-align:center; display:flex; align-items:center; justify-content:center; height:34px; font-size:.85rem; color:#4a5568; cursor:pointer; border-radius:2px; transition:all .12s; }
  .ea-time-opt:hover { background:rgba(14,127,187,.1); color:#043463; }
  .ea-time-opt.active { background:#043463; color:#fff; font-weight:700; }
  .ea-time-sep { display:flex; align-items:center; justify-content:center; font-family:'Bebas Neue',sans-serif; font-size:1.1rem; color:#c3cbd6; width:8px; }
  .ea-time-done { width:100%; margin-top:.85rem; padding:.55rem; border:none; border-radius:2px; background:#ee4c05; color:#fff; font-size:.7rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; cursor:pointer; }
  .ea-time-done:hover { background:#043463; }
`;

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['S','M','T','W','T','F','S'];

const toISODate = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const parseISODate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return { y, m: m - 1, d };
};
const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};
const fmtDatePretty = (iso) => {
  const p = parseISODate(iso);
  if (!p) return '';
  return `${p.d} ${MONTH_NAMES[p.m].slice(0, 3)} ${p.y}`;
};

const isPastEvent = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr) < today;
};

// Close a popover on outside click / Escape
function useOutsideClose(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handle);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);
  return ref;
}

// ---------- Calendar date picker ----------
function DatePicker({ value, onChange, label = 'Select date', minToday = false, required }) {
  const [open, setOpen] = useState(false);
  const sel = parseISODate(value);
  const now = new Date();
  const [viewY, setViewY] = useState(sel ? sel.y : now.getFullYear());
  const [viewM, setViewM] = useState(sel ? sel.m : now.getMonth());
  const ref = useOutsideClose(open, () => setOpen(false));

  useEffect(() => {
    if (open && sel) { setViewY(sel.y); setViewM(sel.m); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);

  const cells = useMemo(() => {
    const firstDow = new Date(viewY, viewM, 1).getDay();
    const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
    const daysInPrev = new Date(viewY, viewM, 0).getDate();
    const arr = [];
    for (let i = firstDow - 1; i >= 0; i--) {
      arr.push({ d: daysInPrev - i, muted: true, y: viewM === 0 ? viewY - 1 : viewY, m: viewM === 0 ? 11 : viewM - 1 });
    }
    for (let d = 1; d <= daysInMonth; d++) arr.push({ d, muted: false, y: viewY, m: viewM });
    while (arr.length % 7 !== 0 || arr.length < 42) {
      const last = arr[arr.length - 1];
      const nd = last.muted && last.m !== viewM ? last.d + 1 : 1;
      arr.push({ d: arr.length - (firstDow + daysInMonth) + 1, muted: true, y: viewM === 11 ? viewY + 1 : viewY, m: viewM === 11 ? 0 : viewM + 1 });
      if (arr.length >= 42) break;
    }
    return arr;
  }, [viewY, viewM]);

  const goMonth = (delta) => {
    let m = viewM + delta, y = viewY;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewM(m); setViewY(y);
  };

  const pick = (cell) => {
    onChange(toISODate(cell.y, cell.m, cell.d));
    setOpen(false);
  };

  return (
    <div className="ea-field" ref={ref}>
      <label className="ea-label">{label}</label>
      <button
        type="button"
        className={`ea-pick-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value ? '' : 'ph'}>{value ? fmtDatePretty(value) : 'Choose a date'}</span>
        <Calendar size={16} />
      </button>
      {required && !value && <input tabIndex={-1} value="" onChange={() => {}} required style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />}

      {open && (
        <div className="ea-popover ea-cal">
          <div className="ea-cal-head">
            <div className="ea-cal-title">{MONTH_NAMES[viewM]} {viewY}</div>
            <div className="ea-cal-nav">
              <button type="button" className="ea-cal-navbtn" onClick={() => goMonth(-1)} aria-label="Previous month"><ChevronLeft size={14} /></button>
              <button type="button" className="ea-cal-navbtn" onClick={() => goMonth(1)} aria-label="Next month"><ChevronRight size={14} /></button>
            </div>
          </div>
          <div className="ea-cal-grid">
            {DOW.map((d, i) => <div key={i} className="ea-cal-dow">{d}</div>)}
            {cells.map((c, i) => {
              const cellDate = new Date(c.y, c.m, c.d); cellDate.setHours(0, 0, 0, 0);
              const disabled = minToday && cellDate < todayMid;
              const isSelected = sel && sel.y === c.y && sel.m === c.m && sel.d === c.d;
              const isToday = cellDate.getTime() === todayMid.getTime();
              return (
                <button
                  type="button"
                  key={i}
                  disabled={disabled}
                  className={`ea-cal-day${c.muted ? ' muted' : ''}${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                  onClick={() => pick(c)}
                >
                  {c.d}
                </button>
              );
            })}
          </div>
          <div className="ea-cal-today-row">
            <button type="button" className="ea-cal-today-btn" onClick={() => { const t = new Date(); pick({ y: t.getFullYear(), m: t.getMonth(), d: t.getDate() }); }}>
              Jump to today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Clock-style time picker ----------
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES_5 = Array.from({ length: 12 }, (_, i) => i * 5); // 0,5,...,55

function to12Hour(hhmm) {
  if (!hhmm) return { h: 9, m: 0, ap: 'AM' };
  const [H, M] = hhmm.split(':').map(Number);
  const ap = H >= 12 ? 'PM' : 'AM';
  let h = H % 12; if (h === 0) h = 12;
  return { h, m: M, ap };
}
function to24HourString(h, m, ap) {
  let H = h % 12;
  if (ap === 'PM') H += 12;
  return `${String(H).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function fmtTimePretty(hhmm) {
  if (!hhmm) return '';
  const { h, m, ap } = to12Hour(hhmm);
  return `${h}:${String(m).padStart(2, '0')} ${ap}`;
}

function TimeColumn({ options, value, onPick, format }) {
  const colRef = useRef(null);
  const itemRefs = useRef({});

  useEffect(() => {
    const el = itemRefs.current[value];
    if (el && colRef.current) {
      colRef.current.scrollTo({ top: el.offsetTop - colRef.current.clientHeight / 2 + el.clientHeight / 2, behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="ea-time-col" ref={colRef}>
      <div className="ea-time-pad" />
      {options.map((opt) => (
        <div
          key={opt}
          ref={(el) => { itemRefs.current[opt] = el; }}
          className={`ea-time-opt${opt === value ? ' active' : ''}`}
          onClick={() => onPick(opt)}
        >
          {format ? format(opt) : opt}
        </div>
      ))}
      <div className="ea-time-pad" />
    </div>
  );
}

function TimePicker({ value, onChange, label = 'Select time', required }) {
  const [open, setOpen] = useState(false);
  const { h, m, ap } = to12Hour(value);
  const ref = useOutsideClose(open, () => setOpen(false));

  const setPart = (part, val) => {
    const next = { h, m, ap, [part]: val };
    onChange(to24HourString(next.h, next.m, next.ap));
  };

  return (
    <div className="ea-field" ref={ref}>
      <label className="ea-label">{label}</label>
      <button
        type="button"
        className={`ea-pick-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value ? '' : 'ph'}>{value ? fmtTimePretty(value) : 'Choose a time'}</span>
        <Clock size={16} />
      </button>
      {required && !value && <input tabIndex={-1} value="" onChange={() => {}} required style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />}

      {open && (
        <div className="ea-popover ea-time">
          <div className="ea-time-face">
            <Clock size={16} className="ea-time-clockicon" />
            <span className="ea-time-display">{fmtTimePretty(value) || '—:—'}</span>
          </div>
          <div className="ea-time-cols">
            <TimeColumn options={HOURS_12} value={h} onPick={(v) => setPart('h', v)} />
            <div className="ea-time-sep">:</div>
            <TimeColumn options={MINUTES_5} value={m - (m % 5) === m ? m : m} onPick={(v) => setPart('m', v)} format={(v) => String(v).padStart(2, '0')} />
            <TimeColumn options={['AM', 'PM']} value={ap} onPick={(v) => setPart('ap', v)} />
          </div>
          <button type="button" className="ea-time-done" onClick={() => setOpen(false)}>Done</button>
        </div>
      )}
    </div>
  );
}

// Confirmation Dialog — restyled to match brand
const ConfirmationDialog = ({ message, onConfirm, onCancel }) => (
  <div className="ea-modal-backdrop">
    <div className="ea-modal">
      <h4>Confirm Deletion</h4>
      <p>{message}</p>
      <div className="ea-modal-actions">
        <button onClick={onCancel} className="ea-btn ea-btn-cancel">No, take me back</button>
        <button onClick={onConfirm} className="ea-btn ea-btn-submit">Yes, delete it</button>
      </div>
    </div>
  </div>
);

const LoadingOverlay = () => (
  <div className="ea-overlay">
    <Loader2 className="ea-spinner w-10 h-10 animate-spin" />
  </div>
);

export default function AdminPage() {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [location, setLocation] = useState('');

  const [existingImages, setExistingImages] = useState([]); // event images from API
  const [newImages, setNewImages] = useState([]); // newly added files
  const [isDragging, setIsDragging] = useState(false);

  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState('');
  const [editingEventId, setEditingEventId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, type: null, id: null });
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [loading, setLoading] = useState(false);

  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback({ message: '', type: '' }), 3000);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [upcomingRes, pastRes] = await Promise.all([
          fetch('https://imarikafoundation.org/api/api/events/upcoming/'),
          // fetch('http://127.0.0.1:8080/api/api/events/upcoming/'),


          fetch('https://imarikafoundation.org/api/api/events/past/'),
          // fetch('http://127.0.0.1:8080/api/api/events/past/'),

        ]);
        if (!upcomingRes.ok || !pastRes.ok) throw new Error('Failed to fetch events');
        const [upcomingEvents, pastEvents] = await Promise.all([
          upcomingRes.json(),
          pastRes.json(),
        ]);
        setEvents([...upcomingEvents, ...pastEvents]);
      } catch (error) {
        console.error('Error fetching events:', error);
        showFeedback('Failed to fetch events.', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Build preview URLs for newly added files, and revoke them when the list
  // changes or the component unmounts, so we don't leak blob URLs.
  const newImagePreviews = useMemo(
    () => newImages.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [newImages]
  );

  useEffect(() => {
    return () => {
      newImagePreviews.forEach(({ url }) => URL.revokeObjectURL(url));
    };
  }, [newImagePreviews]);

  const addImages = (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (files.length) setNewImages((prev) => [...prev, ...files]);
  };

  const handleImageChange = (e) => {
    addImages(e.target.files);
    e.target.value = ''; // allow re-selecting the same file
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addImages(e.dataTransfer.files);
  };

  const handleDropzoneKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const removeExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
  };

  const removeNewImage = (file) => {
    setNewImages((prev) => prev.filter((img) => img !== file));
  };

  const resetForm = () => {
    setEditingEventId(null);
    setTitle('');
    setDescription('');
    setEventDate('');
    setStartTime('');
    setEndTime('');
    setLocation('');
    setExistingImages([]);
    setNewImages([]);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();

    if (!eventDate || !startTime || !endTime) {
      showFeedback('Please pick a date, start time, and end time.', 'error');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('event_date', eventDate);
    formData.append('start_time', startTime);
    formData.append('end_time', endTime);
    formData.append('location', location);

    existingImages.forEach((img) => formData.append('existing_images', img.id));
    newImages.forEach((img) => formData.append('images', img));

    const url = editingEventId
      ? `https://imarikafoundation.org/api/api/events/${editingEventId}/`
      // ? `http://127.0.0.1:8080/api/api/events/${editingEventId}/`

      : 'https://imarikafoundation.org/api/api/events/create-with-images/';

      // : 'http://127.0.0.1:8080/api/api/events/create-with-images/';

    const method = editingEventId ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, { method, body: formData });
      if (!res.ok) throw new Error('Error submitting event');
      const wasEditing = !!editingEventId;
      resetForm();
      showFeedback(wasEditing ? 'Event updated successfully!' : 'Event created successfully!');

      const [upcomingRes, pastRes] = await Promise.all([
        fetch('https://imarikafoundation.org/api/api/events/upcoming/'),
        // fetch('http://127.0.0.1:8080/api/api/events/upcoming/'),

        fetch('https://imarikafoundation.org/api/api/events/past/'),
        // fetch('http://127.0.0.1:8080/api/api/events/past/'),
      ]);
      const [upcomingEvents, pastEvents] = await Promise.all([upcomingRes.json(), pastRes.json()]);
      setEvents([...upcomingEvents, ...pastEvents]);
    } catch (error) {
      console.error('Error submitting event:', error);
      showFeedback('Failed to submit event.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`https://imarikafoundation.org/api/api/events/${id}/`, { method: 'DELETE' });
      // const res = await fetch(`http://127.0.0.1:8080/api/api/events/${id}/`, { method: 'DELETE' });

      if (!res.ok) throw new Error('Failed to delete event');
      showFeedback('Event deleted successfully!');
      const [upcomingRes, pastRes] = await Promise.all([
        fetch('https://imarikafoundation.org/api/api/events/upcoming/'),
        // fetch('http://127.0.0.1:8080/api/api/events/upcoming/'),

        fetch('https://imarikafoundation.org/api/api/events/past/'),
        // fetch('http://127.0.0.1:8080/api/api/events/past/'),

      ]);
      const [upcomingEvents, pastEvents] = await Promise.all([upcomingRes.json(), pastRes.json()]);
      setEvents([...upcomingEvents, ...pastEvents]);
    } catch (error) {
      console.error(error);
      showFeedback('Failed to delete event.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const editEvent = (event) => {
    setTitle(event.title);
    setDescription(event.description);
    setEventDate(event.event_date);
    setStartTime(event.start_time || '');
    setEndTime(event.end_time || '');
    setLocation(event.location);
    setExistingImages(event.images || []);
    setNewImages([]);
    setEditingEventId(event.id);
    setTimeout(
      () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      100
    );
  };

  const confirmDelete = () => {
    if (confirmDialog.type === 'event') deleteEvent(confirmDialog.id);
    setConfirmDialog({ show: false, type: null, id: null });
  };

  const filteredEvents = events
    .filter((ev) => {
      const q = search.toLowerCase();
      return !q || ev.title?.toLowerCase().includes(q) || ev.location?.toLowerCase().includes(q);
    })
    .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));

  return (
    <div className="ea-root">
      <style>{EA_CSS}</style>

      {feedback.message && (
        <div className={`ea-toast ${feedback.type}`}>{feedback.message}</div>
      )}
      {loading && <LoadingOverlay />}

      <div className="ea-shell">
        <div className="ea-topnav">
          <button onClick={() => navigate('/')} className="ea-topbtn">
            <Globe size={16} /> Website
          </button>
          <button onClick={() => navigate('/admin')} className="ea-topbtn primary">
            <LayoutDashboard size={16} /> Admin Home
          </button>
        </div>

        <h1 className="ea-h1">Event <span>Management</span></h1>

        {/* Form */}
        <form ref={formRef} onSubmit={handleEventSubmit} className="ea-card ea-form" encType="multipart/form-data">
          <h3 className="ea-form-h">
            <span className="dot" />
            {editingEventId ? 'Edit Event' : 'Add New Event'}
          </h3>

          <div className="ea-field">
            <label className="ea-label">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title"
              className="ea-input"
              required
            />
          </div>

          <div className="ea-field">
            <label className="ea-label">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this event about?"
              className="ea-textarea"
              required
            />
          </div>

          <div className="ea-row3">
            <DatePicker value={eventDate} onChange={setEventDate} label="Date" minToday required />
            <TimePicker value={startTime} onChange={setStartTime} label="Start Time" required />
            <TimePicker value={endTime} onChange={setEndTime} label="End Time" required />
          </div>

          <div className="ea-field">
            <label className="ea-label">Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Venue or address"
              className="ea-input"
              required
            />
          </div>

          <div className="ea-field">
            <label className="ea-label">Event Photos</label>
            <div
              className={`ea-dropzone${isDragging ? ' drag-active' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onKeyDown={handleDropzoneKeyDown}
              role="button"
              tabIndex={0}
              aria-label="Upload event photos"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageChange}
                className="ea-dropzone-input"
                tabIndex={-1}
              />
              <ImageIcon size={20} className="ea-dropzone-icon" />
              <div className="ea-dropzone-text"><b>Click to upload</b> or drag photos here</div>
            </div>

            {(existingImages.length > 0 || newImagePreviews.length > 0) && (
              <div className="ea-thumbs">
                {existingImages.map((img) => (
                  <div key={`ex-${img.id}`} className="ea-thumb-wrap">
                    <img src={img.image} alt="Event" className="ea-thumb" />
                    <button type="button" onClick={() => removeExistingImage(img.id)} className="ea-thumb-remove" aria-label="Remove image">
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {newImagePreviews.map(({ file, url }, idx) => (
                  <div key={`new-${idx}`} className="ea-thumb-wrap">
                    <img src={url} alt="New" className="ea-thumb" />
                    <button type="button" onClick={() => removeNewImage(file)} className="ea-thumb-remove" aria-label="Remove image">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ea-actions">
            {editingEventId && (
              <button type="button" onClick={resetForm} className="ea-btn ea-btn-cancel">
                Cancel
              </button>
            )}
            <button type="submit" disabled={loading} className="ea-btn ea-btn-submit">
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  {editingEventId ? 'Updating…' : 'Creating…'}
                </>
              ) : editingEventId ? (
                'Update Event'
              ) : (
                'Create Event'
              )}
            </button>
          </div>
        </form>

        {/* List */}
        <div className="ea-list-h-row">
          <h3 className="ea-list-h">All Events</h3>
          <div className="ea-search">
            <input
              type="search"
              placeholder="Search events…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="ea-card ea-empty">
            {search ? 'No events match your search.' : 'No events yet — add your first one above.'}
          </div>
        ) : (
          <div className="ea-list">
            {filteredEvents.map((event) => {
              const past = isPastEvent(event.event_date);
              return (
                <div key={event.id} className="ea-card ea-item">
                  <div className="ea-item-left">
                    <div className="ea-item-top">
                      <span className={`ea-badge ${past ? 'past' : 'upcoming'}`}>
                        {past ? 'Past' : 'Upcoming'}
                      </span>
                      <span className="ea-name">{event.title}</span>
                    </div>
                    <div className="ea-meta">
                      <span className="ea-meta-item"><Calendar size={12} /> {fmtDate(event.event_date)}</span>
                      {event.start_time && (
                        <span className="ea-meta-item"><Clock size={12} /> {event.start_time}{event.end_time ? ` – ${event.end_time}` : ''}</span>
                      )}
                      {event.location && (
                        <span className="ea-meta-item"><MapPin size={12} /> {event.location}</span>
                      )}
                    </div>
                  </div>

                  <div className="ea-item-actions">
                    <button onClick={() => editEvent(event)} className="ea-mini-btn ea-mini-edit">
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => setConfirmDialog({ show: true, type: 'event', id: event.id })}
                      className="ea-mini-btn ea-mini-del"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {confirmDialog.show && (
          <ConfirmationDialog
            message="Are you sure you want to delete this event? This can't be undone."
            onConfirm={confirmDelete}
            onCancel={() => setConfirmDialog({ show: false, type: null, id: null })}
          />
        )}
      </div>
    </div>
  );
}